import { create } from "zustand";
import axios from "axios";
import { backend_url } from "../constants";
import { AXIOS_CONFIG } from "../utils/api";
import { Endpoints } from "../utils/api/endpoints";

/**
 * Centralized type for all feature flags in the application.
 * Add new feature flags here to ensure type safety across the app.
 */
export type FeatureFlags =
  | "TAXWISE_PURCHASE"
  | "DRAKE_PURCHASE"
  | "DRAKE_DESKTOP_PURCHASE"
  | "OLT_PURCHASE"
  | "CROSSLINK_PURCHASE"
  | "SBTPG_PARTNERSHIP_ANNOUNCEMENT"
  | "BROWSER_ALLOW_EDGE"
  | "PAY_PER_RETURN"
  | "PROMO"
  | "LEGACY_PAYMENTS"
  | "CONTRACT_UPGRADE_PROMPT"
  | "CONTRACT_WAIVE_DEPOSIT"
  | "FREE_TAX_SOFTWARE_ADD_ON_COUPON";

interface FeatureFlagsResponse {
  content: {
    flags: string[];
    flagDisplayMessages: Record<string, string>;
    userEmail: string;
  };
}

interface FeatureFlagStore {
  flags: Set<string>;
  flagDisplayMessages: Record<string, string>;
  isInitialized: boolean;
  loading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  setFlags: (flags: string[]) => void;
  setFlagDisplayMessages: (displayMessages: Record<string, string>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  hasFlag: (flag: FeatureFlags) => boolean;
  getDisplayMessage: (flag: FeatureFlags) => string | null;
  reset: () => void;
}

export const useFeatureFlagStore = create<FeatureFlagStore>((set, get) => ({
  flags: new Set<string>(),
  flagDisplayMessages: {},
  isInitialized: false,
  loading: false,
  error: null,

  initialize: async () => {
    const { isInitialized, loading } = get();

    // Prevent duplicate initialization
    if (isInitialized || loading) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await axios.get<FeatureFlagsResponse>(
        `${backend_url}/${Endpoints.featureFlags}`,
        AXIOS_CONFIG
      );

      const flagsSet = new Set<string>(response.data.content.flags);
      const displayMessages = response.data.content.flagDisplayMessages || {};

      set({
        flags: flagsSet,
        flagDisplayMessages: displayMessages,
        isInitialized: true,
        loading: false,
        error: null,
      });

      console.log(
        `[FeatureFlagStore] Feature flags loaded: ${Array.from(flagsSet).join(", ")}`
      );
    } catch (err) {
      const errorMessage = "Failed to fetch feature flags";
      console.error("[FeatureFlagStore] Error fetching feature flags:", err);

      set({
        flags: new Set<string>(),
        flagDisplayMessages: {},
        isInitialized: false,
        loading: false,
        error: errorMessage,
      });

      // Don't throw - allow the app to continue with no feature flags
    }
  },

  setFlags: (flags) =>
    set({ flags: new Set(flags), error: null, isInitialized: true }),
  setFlagDisplayMessages: (flagDisplayMessages) => set({ flagDisplayMessages }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  hasFlag: (flag) => get().flags.has(flag),
  getDisplayMessage: (flag) => get().flagDisplayMessages[flag] || null,
  reset: () =>
    set({
      flags: new Set<string>(),
      flagDisplayMessages: {},
      isInitialized: false,
      loading: false,
      error: null,
    }),
}));
